import { Store } from 'vuex';
import axios, { AxiosError, AxiosResponse } from 'axios';
import { Lang, Skill } from '@/store/types';
import xml2js from 'xml2js';

export default {
  DEFAULT_LANG_I: {
    id: '297',
    lang: 'English',
  } as Lang,

  LS_GLOBAL_VOLUME: 'LS_GLOBAL_VOLUME_BB',
  LS_REMEMBER_CREDENTIALS: 'LS_REMEMBER_CREDENTIALS_BB',
  LS_LAST_USERNAME: 'LS_LAST_USERNAME_BB',
  LS_LAST_PASSWORD: 'LS_LAST_PASSWORD_BB',
  LS_LAST_LANG_I: 'LS_LAST_LANG_I',
  REPEAT_RECORDED: 3,

  APP_LINK_IOS: 'https://apps.apple.com/us/app/bluebird-learn-163-languages/id1484286928',
  APP_LINK_ANDROID: 'https://play.google.com/store/apps/details?id=com.pronunciatorllc.bluebird',
  WEB_LINK_HELP: 'https://bluebirdlanguages.com/faq/',
  WEB_LINK_EMAIL: 'mailto:android@bluebirdlanguages.com',
  WEB_LINK_TWITTER: 'https://mobile.twitter.com/BluebirdLangua1',
  WEB_LINK_TERMS_OF_USE: 'https://bluebirdlanguages.com/bluebird-languages-terms-of-use/',
  WEB_LINK_PRIVACY_POLICY: 'https://bluebirdlanguages.com/bluebird-languages-privacy-policy/',

  bbHeaders: {
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded',
      'Cache-Control': 'no-cache',
    },
    withCredentials: true,
  },

  AUDIO_LOADING_CANCEL: 'Audio loading canceled',

  APP_POSTFIX: '?app=bluebird',

  getDefaultParams(): URLSearchParams {
    const randomNumber = Math.round(Math.random() * 100000);
    return new URLSearchParams(`device=5&r=${randomNumber}`);
  },

  getLinkFromParams(url: string, params: URLSearchParams): string {
    let resultLink = `${url}?`;
    params.forEach((param) => {
      resultLink += `${param[0]}=${param[1]}&`;
    });
    return resultLink;
  },

  checkSuccess(response: AxiosResponse, expectedMsg: string | null, store: Store<any>): void {
    store.commit('setMainLoading', false);
    const responseData = response.data;
    if (!responseData.success || responseData.error || responseData.message !== expectedMsg) {
      throw new Error(responseData.message || `Unknown Error (${expectedMsg})`);
    }
  },

  checkSuccessParams(params: URLSearchParams, expectedMsg: string | null, store: Store<any>): void {
    store.commit('setMainLoading', false);
    if (!params.get('success') || params.get('error') === '1'
      || params.get('message') !== expectedMsg) {
      throw new Error(params.get('message') || `Unknown Error (${expectedMsg})`);
    }
  },

  handleError(error: AxiosError, message: string, store: Store<any>): string {
    store.commit('setMainLoading', false);
    const errorMessage = error.message || (error.response ? error.response.data : message);
    return errorMessage;
  },

  emailIsOkay(value: string): boolean {
    const pattern = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
    return pattern.test(value);
  },

  getRandomInt(max: number): number {
    return Math.floor(Math.random() * Math.floor(max)) + 1;
  },

  generateRandomString(length: number) {
    const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
    let result = '';
    const charactersLength = characters.length;
    for (let i = 0; i < length; i += 1) {
      result += characters.charAt(Math.floor(Math.random() * charactersLength));
    }
    return result;
  },

  compareByOrder(a: any, b: any): number {
    const orderA = parseInt(a.order, 10);
    const orderB = parseInt(b.order, 10);
    if (orderA > orderB) {
      return 1;
    }
    return (orderA > orderB) ? -1 : 0;
  },

  getILangAsLLang(iLangId: string): string {
    if (iLangId === '297') {
      return '1';
    }
    if (iLangId === '298') {
      return '3';
    }
    if (iLangId === '299') {
      return '10';
    }
    return iLangId;
  },

  replaceAll(source: string, search: string | RegExp, replacement: string): string {
    if (source) {
      return source.replace(new RegExp(search, 'g'), replacement);
    }
    return '';
  },

  // always cancel this promise in beforeDestroy to clear sleepPromise in store
  sleepCancelable(ms: number, store: Store<any>) {
    this.clearSleep(store);
    const cancelableObject = this
      .makeCancelable(new Promise((resolve) => setTimeout(resolve, ms)), store);
    store.commit('setSleepPromise', cancelableObject);
    return cancelableObject.promise;
  },

  clearSleep(store: Store<any>) {
    if (store.state.sleepPromise) {
      store.state.sleepPromise.cancel();
    }
  },

  makeCancelable(promise: Promise<void>, store: Store<any>) {
    let hasCanceled = false;
    const wrappedPromise = new Promise((resolve, reject) => {
      promise.then((val) => {
        store.commit('setSleepPromise', null);
        if (hasCanceled) {
          // eslint-disable-next-line prefer-promise-reject-errors
          reject({ isCanceled: true });
        } else {
          resolve(val);
        }
      });
      promise.catch((error) => {
        store.commit('setSleepPromise', null);
        if (hasCanceled) {
          // eslint-disable-next-line prefer-promise-reject-errors
          reject({ isCanceled: true });
        } else {
          reject(error);
        }
      });
    });
    // the lines below are commented out to interrupt executing code after cancelation
    /* .catch((e) => {
      console.log('catched', e)
    }) */
    return {
      promise: wrappedPromise,
      cancel() {
        hasCanceled = true;
      },
    };
  },

  async loadVideoLanguages(store: Store<any>): Promise<void> {
    const videoLanguages = new Map<string, Array<string>>();
    await axios.get(store.getters.URL_VIDEO_LANGUAGES, this.bbHeaders)
      .then((response: AxiosResponse) => {
        try {
          let responseText: string = response.data;
          responseText = this.replaceAll(responseText, '<', '<lang');
          responseText = this.replaceAll(responseText, '<lang/', '</lang');
          xml2js.parseString(responseText, (error: Error, result: any) => {
            if (result) {
              result = result['langvideo-languages'];
              Object.keys(result).forEach((key: string) => {
                if (result[key] && result[key][0]) {
                  videoLanguages.set(this.replaceAll(key, 'lang', ''), result[key][0]
                    .split('-'));
                }
              });
            } else {
              store.commit('setAlertMessage', { text: 'Video languages error #1', error });
            }
          });
        } catch (error) {
          store.commit('setAlertMessage', { text: 'Video languages error #2', error });
        }
      })
      .catch((error: AxiosError) => {
        store.commit('setAlertMessage', { text: 'Video languages error #3', error });
      });
    store.commit('setVideoLanguages', videoLanguages);
  },

  async loadTranslations(store: Store<any>, langI: Lang): Promise<void> {
    const translationsDefaultClone = { ...store.state.translationsDefault };
    await axios.get(`${store.getters.URL_TRANSLATIONS}${langI.id}.xml`, this.bbHeaders)
      .then((response: AxiosResponse) => {
        try {
          xml2js.parseString(response.data, (error: Error, result: any) => {
            if (result) {
              const tr = result.ProMenu;
              // default translations are used for keys that don't exist in loaded translations
              // if key exists in loaded translation - use it instead of default
              Object.keys(tr).forEach((key: string) => {
                tr[key] = this.replaceAll(tr[key][0], /\\n/, '');
                tr[key] = this.replaceAll(tr[key], /\\'/, '\'');
                translationsDefaultClone[key] = tr[key];
              });
            } else {
              store.commit('setAlertMessage', { text: 'Translations error #1', error });
            }
          });
        } catch (error) {
          store.commit('setAlertMessage', { text: 'Translations error #2', error });
        }
      })
      .catch((error: AxiosError) => {
        store.commit('setAlertMessage', { text: 'Translations error #3', error });
      });
    store.commit('setTranslations', translationsDefaultClone);
  },

  async loadLanguagesILearn(store: Store<any>, langI: Lang): Promise<void> {
    const params = this.getDefaultParams();
    params.append('ilang', langI.id);
    await axios.post(store.getters.URL_LANGUAGES_ILEARN, params, this.bbHeaders)
      .then((response: AxiosResponse) => {
        const responseJson = response.data;
        if (responseJson.success !== true) {
          store.commit('setAlertMessage', { text: 'Languages ILearn error #1' });
          return;
        }
        const langsL = Array<Lang>();
        responseJson.data.forEach((lang: Lang) => {
          lang.lang = lang.text;
          if (lang.id !== '1015' && lang.id !== '1014') {
            langsL.push(lang);
          }
        });
        store.commit('setLanguagesL', langsL);
      })
      .catch((error) => {
        store.commit('setAlertMessage', { text: 'Languages ILearn error #2', error });
      });
  },

  async loadSkillLevels(store: Store<any>, langI: Lang) {
    await axios.get(store.getters.URL_SKILLS, this.bbHeaders)
      .then(async (response: AxiosResponse) => {
        await xml2js.parseStringPromise(response.data).then((result: any) => {
          if (result && result.skills) {
            const skills = Array<Skill>();
            result.skills.skill.forEach((item: any) => {
              let tr = null;
              let defaultTr = null;
              item.lang.forEach((lang: any) => {
                if (lang.$.id === '297') {
                  defaultTr = lang._;
                } else if (lang.$.id === langI.id) {
                  tr = lang._;
                }
              });
              if (!tr) {
                tr = defaultTr;
              }
              if (tr) {
                const skill = {
                  category: item.$.category,
                  id: item.$.id,
                  order: parseInt(item.$.order, 10),
                  uri: item.$.imagebase
                    ? `${store.getters.ipLearn}${item.$.imagebase}-hq.jpg${this.APP_POSTFIX}`
                    : `${store.getters.ipLearn}${item.$.image}${this.APP_POSTFIX}`,
                  label: tr,
                };
                skills.push(skill);
              }
            });
            skills.sort((a, b) => a.order - b.order);
            store.commit('setSkills', skills);
          } else {
            store.commit('setAlertMessage', { text: 'Skills error #1' });
          }
        }).catch((error) => {
          store.commit('setAlertMessage', { text: 'Skills error #2', error });
        });
      })
      .catch((error: Error) => {
        store.commit('setAlertMessage', { text: 'Skills error #3', error });
      });
  },

  async loadLevelsAllowed(store: Store<any>): Promise<void> {
    await axios.get(`${store.getters.URL_LEVELS_ALLOWED}`, this.bbHeaders)
      .then((response: AxiosResponse) => {
        let responseText = this.replaceAll(response.data, '<', '<lang');
        responseText = this.replaceAll(responseText, '<lang/', '</lang');
        try {
          xml2js.parseString(responseText, (error: Error, result: any) => {
            if (result) {
              const levelsAllowed = result['langlevels-ready'];
              Object.keys(levelsAllowed).forEach((lang: string) => {
                if (levelsAllowed[lang] && levelsAllowed[lang][0]) {
                  levelsAllowed[lang] = levelsAllowed[lang][0].split('-');
                }
              });
              store.commit('setLevelsAllowed', levelsAllowed);
            } else {
              store.commit('setAlertMessage', {
                text: 'Levels allowed error #1',
                error,
              });
            }
          });
        } catch (error) {
          store.commit('setAlertMessage', {
            text: 'Levels allowed error #2',
            error,
          });
        }
      })
      .catch((error: AxiosError) => {
        store.commit('setAlertMessage', {
          text: 'Levels allowed error #3',
          error,
        });
      });
  },

  loadPostcardsImages(store: Store<any>): Promise<void> {
    const params = this.getDefaultParams();
    return axios.post(store.getters.URL_POSTCARDS_IMAGES, params, this.bbHeaders)
      .then((response: AxiosResponse) => {
        let responseData = response.data;
        responseData = this.replaceAll(responseData, '<', '<lang');
        responseData = this.replaceAll(responseData, '<lang/', '</lang');
        try {
          xml2js.parseString(responseData, (error: Error, result: any) => {
            if (result) {
              const postcardsImages = new Map<string, Array<string>>();
              Object.keys(result.langpostcards).forEach((key: string) => {
                if (result.langpostcards[key] && result.langpostcards[key][0]) {
                  postcardsImages.set(this.replaceAll(key, 'lang', ''),
                    result.langpostcards[key][0]);
                }
              });
              store.commit('setPostcardsImages', postcardsImages);
            } else {
              store.commit('setAlertMessage', {
                text: 'Postcard images error #1',
                error,
              });
            }
          });
        } catch (error) {
          store.commit('setAlertMessage', {
            text: 'Postcard images error #2',
            error,
          });
        }
      })
      .catch((error: AxiosError) => {
        store.commit('setAlertMessage', {
          text: 'Postcard images error #3',
          error,
        });
      });
  },

};
